// D:\DhammaKnowledgeBase\chat-ui\src\context\AuthContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebaseConfig';
import { getOrCreateUserDocument } from '../auth/authService';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [userDataLoading, setUserDataLoading] = useState(true);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      // Log the authentication state change
      console.log("AuthContext: onAuthStateChanged - user:", user ? user.email : "Logged Out");
      setCurrentUser(user);
      setAuthLoading(false); // Auth state is now loaded

      if (user) {
        setUserDataLoading(true); // Start loading user data
        const userDocRef = doc(db, 'users', user.uid);

        // First, explicitly ensure the document exists or is created
        // This is crucial for new users logging in for the very first time
        try {
          const initialUserData = await getOrCreateUserDocument(user);
          // Log the data returned immediately after getOrCreateUserDocument
          console.log("AuthContext: Initial user data from getOrCreateUserDocument:", initialUserData);
          setUserData(initialUserData); // Set data immediately after creation/fetch
        } catch (error) {
          console.error("AuthContext: Error ensuring user document exists:", error);
          setUserData(null);
        } finally {
          // Regardless of initial fetch/create, set userDataLoading to false
          // *after* initial attempt.
          setUserDataLoading(false);
        }

        // Now, set up the real-time listener for future updates
        const unsubscribeFirestore = onSnapshot(userDocRef, (docSnap) => {
          if (docSnap.exists()) {
            const fetchedData = docSnap.data();
          // Log the data received from the real-time Firestore listener
          console.log("AuthContext: onSnapshot - fetched data:", fetchedData);

          // --- BEGIN DAILY RESET LOGIC ---
          const now = new Date();
          const lastReset = fetchedData.lastQueryReset ? fetchedData.lastQueryReset.toDate() : new Date(0);
          const needsReset = now.setHours(0, 0, 0, 0) > lastReset.setHours(0, 0, 0, 0);

          if (needsReset) {
            console.log("AuthContext: Daily query limit reset needed.");
            let maxQueries;
            switch (fetchedData.tier) {
              case 'Samādhi':
                maxQueries = 100;
                break;
              case 'Paññā':
                maxQueries = 1000;
                break;
              case 'Sīla':
              default:
                maxQueries = 5;
            }
            // Update the document in Firestore
            setDoc(userDocRef, { 
              ...fetchedData, 
              queriesLeft: maxQueries, 
              lastQueryReset: serverTimestamp() 
            }, { merge: true });
            // No need to call setUserData here, as the onSnapshot listener will fire again with the updated data.
          } else {
            setUserData(fetchedData);
          }
          // --- END DAILY RESET LOGIC ---
          } else {
            // This case should ideally not happen if getOrCreateUserDocument worked,
            // but if the document somehow disappears, it will clear userData.
            console.warn("AuthContext: User document no longer exists for:", user.uid);
            setUserData(null);
          }
        }, (error) => {
          console.error("AuthContext: Error fetching user data via onSnapshot:", error);
          setUserData(null);
          // No need to set userDataLoading here, as it's already false from initial load
        });

        return () => unsubscribeFirestore(); // Cleanup Firestore listener
      } else {
        // User logged out
        console.log("AuthContext: User logged out, clearing user data.");
        setUserData(null);
        setUserDataLoading(false); // No user, no data to load
      }
    });

    return () => unsubscribeAuth(); // Cleanup auth listener
  }, []); // Empty dependency array means this runs once on component mount

  const value = {
    currentUser,
    userData,
    loading: authLoading, // Overall authentication state loading
    userDataLoading, // User-specific data loading from Firestore
  };

  return (
    <AuthContext.Provider value={value}>
      {/* Render children only when both auth and user data are loaded */}
      {(!authLoading && !userDataLoading) && children}
      {/* Optionally, show a loader if either is still loading */}
      {(authLoading || userDataLoading) && <div className="loading-screen">Loading application...</div>}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
